import * as React from 'react';
import _ from 'lodash';
import Joi from 'joi-browser';
import { SEARCH_PARAMS_SESSION_PAGE } from '@bld/bld-libs/constants';

import masterSlideLoader from '../utilities/masterSlideLoader';
import { UserProfileService } from '../services/UserProfiles';
import {
  USER_ROLES,
  FIELD_COMPLETE_STATUS,
  BUILDEE_SERVICES,
  INVITATION_STATUS,
} from '../utilities/constants';
import AuthProvider from '../utilities/authProvider';
import EventManager, { EventType } from '../utilities/EventManager';
import LocalStorageService from '../services/utilities/LocalStorageService';
import SessionStorageService from '../services/utilities/SessionStorageService';
import { ChooseUserIntegrateModal } from './modals/ChooseUserIntegrateModal';
import { DirectLinkModal } from './modals/DirectLinkModal';
import { InvitationConfirmDialog } from './modals/InvitationConfirmDialog';
import { MisregistrationReportDialog } from './modals/MisregistrationReportDialog';
import { CompanyUserInvitationStatusService } from '../services/CompanyUserInvitationStatuses';
import { LoginUserGroupsService } from '../services/LoginUserGroups';
import { isPartnerRoleId, isForemanRoleId } from '../utilities/roleUtility';
import { setFieldNameForGTM, setListPrimeCompanyIdsForGTM } from '../utilities/gtm';
import { CompanyBranchService } from '../services/CompanyBranches';
import { SessionsService } from '../services/Sessions';
import {
  getCompanyId,
  checkCallBackGreenFileService,
  directLinkAction,
  getGreenFileSystemUrl,
} from '../utilities/commonUtil';
import { userIntegrateTableActionParam } from './tables/UserIntegrateTable';
import {
  createFieldsDefaultSearchParams,
  saveFieldData,
  getAvailableServiceId,
} from '../utilities/fieldUtil';
import { FieldService } from '../services/Fields';
import CompanyService from '../services/Companies';
import {
  getPermissionByServiceIdAndCompanyId,
  hasOnlyRouanPermission,
} from '../utilities/companyUserPermissionUtil';
import Information from './modals/common/Information';
import { validateByJoi } from '../utilities/validation';
import { messageUtility } from '../utilities/messageUtility';
import { deleteControlCode } from '../utilities/stringUtil';

const DIRECT_LINK_STATE = {
  NONE: 'NONE',
  INVALID: 'INVALID',
};

/**
 * 右上のアカウントのメニュー
 *
 * @export
 * @class AccountMenu
 * @extends {React.Component}
 */
export class AccountMenu extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      closed: true,
      user: {
        name: SessionStorageService.getItem('userName') || '',
        jobTitle: SessionStorageService.getItem('jobTitle') || '',
      },
      userId: LocalStorageService.getUserId(),
      userName: '',
      jobName: '',
      roleId: LocalStorageService.getRoleId(),
      integrated: false,
      usersIntegrate: [],
      partnerCompanies: [],
      directLink: null,
      directLinkUserIntegrate: null,
      directLinkPartnerCompany: null,
      directLinkState: DIRECT_LINK_STATE.NONE,
      fieldSwitchData: null,
      isShowSwitchUser: false,
      showBtnClose: false,
      afterLoginEventsFlag: !!SessionStorageService.getAfterLoginEventsFlag(),
      // アイコン画像
      imageUrl: '',
      isShowSwitchCompany: false,
      isShowSwitchDirectLinkUser: false,
      isShowSwitchDirectLinkCompany: false,
      isShowInvitationConfirm: false,
      isShowMisregistrationReport: false,
      invitationEntities: {},
      selectedCompany: null,
      misregistrationReportValues: {
        invitation_status: INVITATION_STATUS.misregistration_reported,
        invitee_comment: '',
        errors: {},
      },
    };

    this.isSubmittingFlag = false;

    document.addEventListener('mousedown', this.handleClick, false);
  }

  async componentDidMount() {
    // アカウント詳細・編集、利用者詳細・編集（協力会社）での更新後に反映させる
    EventManager.addEventListener(EventType.REFRESH_PROFILE, this.getUserProfileEventListener);
    EventManager.addEventListener(EventType.SHOW_SWITCH_USER_INTEGRATE, () => {
      this.showSwitchUser();
    });
    EventManager.addEventListener(EventType.SHOW_SWITCH_COMPANY, () => {
      this.showSwitchCompany();
    });
    await this.getUserProfile();
    const partnerCompanies = await this.getPartnerList();
    const directLink = SessionStorageService.getDirectLink();
    const selectedCompanyButUnconfirmedFlag = await this.selectedCompanyButUnconfirmed(partnerCompanies);
    if (this.state.afterLoginEventsFlag || directLink || selectedCompanyButUnconfirmedFlag) {
      if (this.state.integrated) {
        this.setState({ directLink }, () => {
          this.showSwitchUserOnAfterLogin();
        });
      } else if (isPartnerRoleId(this.state.roleId)) {
        this.setState({ directLink }, () => {
          this.showSwitchCompanyOnAfterLogin(partnerCompanies);
        });
      } else if (directLink) {
        this.setState({ directLink }, () => {
          this.showSwitchFieldOnAfterLogin();
        });
      }
    } else {
      this.setState({ showBtnClose: true });
    }
  }

  componentWillUnmount() {
    const afterSwitchUser = SessionStorageService.getAfterSwitchUser();
    if (!afterSwitchUser) {
      EventManager.removeEventListener(EventType.REFRESH_PROFILE, this.getUserProfileEventListener);
    }

    document.removeEventListener('mousedown', this.handleClick, false);
  }

  /**
   * ユーザープロフィール取得
   *
   * @memberof AccountMenu
   */
  getUserProfile() {
    return UserProfileService.show(this.state.userId, {}).then(response => {
      if (!response.result) {
        return;
      }

      let imageUrl = '';
      if (_.has(response.data.entities, 'image')) {
        imageUrl = response.data.entities.image.image_url;
      }

      const userName = `${response.data.entities.user_profile.name_sei} ${
        response.data.entities.user_profile.name_mei
      }`;
      const roleCode = response.data.entities.role_code;
      const roleName = _.find(USER_ROLES, role => {
        return role.id === roleCode;
      });
      const integrated =
        _.get(response.data, 'entities.user_profile.integrated') || false;
      this.setState({ userName, jobName: roleName.name, imageUrl, integrated });
    });
  }

  getUserProfileEventListener = this.getUserProfile.bind(this);

  handleClick = e => {
    if (this.node.contains(e.target)) {
      return;
    }

    this.handleClickOutside();
  };

  handleClickOutside = () => {
    this.setState({ closed: true });
  };

  handleToggle = () => {
    this.setState({ closed: !this.state.closed });
  };

  getCompanyUserInvitationStatus = async invitee_company_id => {
    const { result, data } = await CompanyUserInvitationStatusService.index({
      invitee_company_id,
    });
    let invitationEntities = null;
    if (result) {
      invitationEntities = _.get(data, 'entities');
    }
    return invitationEntities;
  };

  getUserIntegrate = async () => {
    const { result, data } = await LoginUserGroupsService.list();
    let userIntegrates = null;
    if (result) {
      userIntegrates = _.get(data, 'entities.users') || [];
      this.setState({ usersIntegrate: userIntegrates });
    }
    return userIntegrates;
  };

  getPartnerList = async () => {
    const { result, data } = await CompanyBranchService.primeList();
    let partnerCompanies = null;
    if (result) {
      partnerCompanies = _.get(data, 'entities.company');
      const { userId, userName, roleId } = this.state;
      partnerCompanies = _.map(partnerCompanies, company => {
        return {
          company: {
            ...company,
            prime_company_id: company.id,
            name: company.partner_company_name,
          },
          company_branches: company.company_branches,
          ...{ id: userId, fullname: userName, user_role: roleId },
        };
      });
      this.setState({ partnerCompanies });
    }
    return partnerCompanies;
  };

  toggleDialog = () => {
    this.setState(
      state => ({ isShowSwitchUser: !state.isShowSwitchUser, closed: true }),
      () => {
        SessionStorageService.removeItem('afterLoginEventsFlag');
        if (this.state.isShowSwitchUser) {
          this.getUserIntegrate();
        } else {
          this.goBackGreenFileService();
        }
      }
    );
  };

  toggleSwitchCompany = () => {
    this.setState(
      state => ({
        isShowSwitchCompany: !state.isShowSwitchCompany,
        closed: true,
      }),
      () => {
        SessionStorageService.removeItem('afterLoginEventsFlag');
        if (this.state.isShowSwitchCompany) {
          this.getPartnerList();
        } else {
          this.goBackGreenFileService();
        }
      }
    );
  };

  selectUser = async data => {
    const { result } = await LoginUserGroupsService.updateUser(data);
    if (result) {
      SessionStorageService.setAfterSwitchUser(true);
      if (isPartnerRoleId(data.roleId)) {
        this.handleStorageWithPartner(data);
      }
      const userId = LocalStorageService.getItem('user_id');
      const directLink = this.state.directLink;
      if (directLink && directLink.user_id === Number(userId)) {
        await this.directLinkJunction(directLink);
        return;
      }
      window.location.href = '/callback';
    } else {
      if (this.state.directLink) {
        this.setState({ directLinkState: DIRECT_LINK_STATE.INVALID });
        return;
      }
    }
  };

  selectCompany = async company => {
    if (!company.partner_company_id) {
      return;
    }
    const { result } = await SessionsService.updatePartner({
      partner_company_id: company.partner_company_id,
    });
    if (result) {
      this.handleStorageWithPartner(company);
      CompanyService.primeList().then(response => {
        setListPrimeCompanyIdsForGTM(_.get(response, 'data.entities.prime_company_ids'));
      });
      if (
        await this.showInvitationConfirm(company.partner_company_id, company)
      ) {
        return;
      }
      SessionStorageService.removeItem('afterLoginEventsFlag');

      // 所属会社を変更する際にセッションストレージに保存している検索条件をクリアする
      SessionStorageService.removeItem(SEARCH_PARAMS_SESSION_PAGE.FIELDS);

      const directLink = this.state.directLink;
      if (directLink) {
        await this.directLinkJunction(directLink);
        return;
      }
      window.location.href = '/conference/Fields/0/0/0/0';
    } else {
      if (this.state.directLink) {
        this.setState({ directLinkState: DIRECT_LINK_STATE.INVALID });
        return;
      }
    }
  };

  showInvitationConfirm = async (company_id, company) => {
    if (
      Number(SessionStorageService.getItem('invitation_status')) ===
      INVITATION_STATUS.confirmed
    ) {
      return false;
    }
    const invitationEntities = await this.getCompanyUserInvitationStatus(
      company_id
    );
    const invitation_status = _.get(
      invitationEntities,
      'company_user_invitation_status.invitation_status'
    );
    SessionStorageService.setItem('invitation_status', invitation_status);
    if (invitation_status === INVITATION_STATUS.unconfirmed) {
      this.setState({
        isShowInvitationConfirm: true,
        isShowSwitchCompany: false,
        invitationEntities,
        selectedCompany: company,
      });
      return true;
    }
    return false;
  };

  directLinkJunction = async directLink => {
    if (
      directLink.field_id &&
      directLink.field_id !== Number(SessionStorageService.getItem('field_id'))
    ) {
      const field = await this.selectedField(directLink);
      if (field) {
        if (!directLink.user_id && !directLink.company_id) {
          this.setState({
            fieldSwitchData: directLink.field,
            isShowSwitchUser: false,
            isShowSwitchCompany: false,
          });
          this.switchField();
          return;
        }
        await this.selectField(field, directLink);
        return;
      } else {
        this.setState({ directLinkState: DIRECT_LINK_STATE.INVALID });
        return;
      }
    } else {
      if (directLink.field_id) {
        let permission = false;
        const field = await this.selectedField(directLink);
        if (field) {
          permission = await this.hasFieldPermission(field, directLink);
        }
        if (!permission) {
          this.setState({ directLinkState: DIRECT_LINK_STATE.INVALID });
          return;
        }

        // BLDE_V2-39552 同じ現場が既に選択されたとしても、現場選択処理は行う
        await this.selectField(field, directLink);
      } else if (directLink.action_path.includes('FieldsDetail')) {
        const fieldId = directLink.action_path.split('/')[2].slice(13);
        let invalidFlag = true;
        await FieldService.list({ field_id: fieldId }).then(response => {
          if (response.data.total === 1) {
            invalidFlag = false;
          }
        });
        if (invalidFlag) {
          this.setState({ directLinkState: DIRECT_LINK_STATE.INVALID });
          return;
        }
        await directLinkAction(directLink);
      } else {
        // その他の場合はdirectLinkで画面遷移
        await directLinkAction(directLink);
      }
      return;
    }
  };

  handleStorageWithPartner = data => {
    if (data.prime_company_id) {
      SessionStorageService.setItem(
        'sessionPrimeCompanyId',
        data.prime_company_id
      );
    }
    SessionStorageService.setSessionPartnerCompanyIdOfPrimeCompany(
      data.partner_company_id
    );
    SessionStorageService.setSessionSelectedPartnerCompanyId(
      data.partner_company_id
    );
    if (data.partner_company_id) {
      SessionStorageService.setItem('company_id', data.partner_company_id);
      SessionStorageService.setItem('prime_company_id', data.partner_company_id);
      let data_company_branch_id = data.company_branches
        ? data.company_branches.id
        : null;
      if (!data_company_branch_id) {
        data_company_branch_id = data.company_branch_id
          ? data.company_branch_id
          : null;
      }
      SessionStorageService.setItem('company_branch_id', data_company_branch_id);
      SessionStorageService.setPartnerCompanyJoiningFlag(
        data.partner_company_joining_flag
      );
    }

    SessionStorageService.removeItem('invitation_status');

    const relayPoint = SessionStorageService.getRelayPoint();
    const directLink = SessionStorageService.getDirectLink();
    if (
      !directLink ||
      (this.state.integrated && !directLink.user_id) ||
      (!this.state.integrated && !directLink.company_id) ||
      relayPoint.switchUserFlag ||
      relayPoint.switchCompanyFlag ||
      (directLink.field_id &&
        directLink.field_id !== Number(SessionStorageService.getItem('field_id')))
    ) {
      SessionStorageService.removeItem('field_tree_id');
      SessionStorageService.removeItem('field_tree_user_id');
      SessionStorageService.removeItem('date_from');
      SessionStorageService.removeItem('date_to');
      SessionStorageService.removeItem('work_hour_zone');
      SessionStorageService.removeItem('working_time');
      SessionStorageService.removeItem('work_start_time');
      SessionStorageService.removeItem('work_start_time_night');
      SessionStorageService.removeItem('work_start_time_midnight');
      SessionStorageService.removeItem('work_end_time');
      SessionStorageService.removeItem('work_end_time_night');
      SessionStorageService.removeItem('work_end_time_midnight');
      SessionStorageService.removeItem('use_gk_flg');
      SessionStorageService.removeItem('use_roan_flg');
      SessionStorageService.removeItem('use_tracker_flg');
      SessionStorageService.removeItem('prime_company_id');
      SessionStorageService.removeItem('field_id');
      SessionStorageService.removeUseKYFlag();
      setFieldNameForGTM('');
    }
  };

  showSwitchUser = () => {
    this.setState({ isShowSwitchUser: true }, () => this.getUserIntegrate());
  };

  showSwitchCompany = () => {
    this.setState({ isShowSwitchCompany: true }, () => this.getPartnerList());
  };

  showSwitchUserOnAfterLogin = async () => {
    const userId = LocalStorageService.getItem('user_id');
    const directLink = this.state.directLink;
    let directLinkUserIntegrate = null;
    if (directLink) {
      if (directLink.user_id) {
        const companyId = SessionStorageService.getItem('company_id');
        directLinkUserIntegrate = await this.selectedUser(
          directLink.user_id,
          directLink.company_id
        );
        if (!directLinkUserIntegrate) {
          this.setState({ directLinkState: DIRECT_LINK_STATE.INVALID });
          return;
        } else if (
          directLink.user_id === Number(userId) &&
          (!directLink.company_id ||
            directLink.company_id === Number(companyId))
        ) {
          if (
            directLink.field &&
            directLink.field.id &&
            directLink.field.id !==
              Number(SessionStorageService.getItem('field_id'))
          ) {
            if (SessionStorageService.getAfterSwitchUser()) {
              await this.directLinkJunction(directLink);
              return;
            }
            this.setState({
              directLinkUserIntegrate,
              fieldSwitchData: directLink.field,
            });
            this.switchUser();
            return;
          } else {
            this.selectUser(
              userIntegrateTableActionParam(directLinkUserIntegrate)
            );
            return;
          }
        } else {
          this.setState({
            isShowSwitchDirectLinkUser: true,
            directLinkUserIntegrate,
            fieldSwitchData: directLink.field,
          });
          return;
        }
      } else {
        if (SessionStorageService.getAfterSwitchUser()) {
          this.showSwitchFieldOnAfterLogin();
          return;
        }
      }
    }
    this.showSwitchUser();
  };

  showSwitchCompanyOnAfterLogin = async partnerCompanies => {
    const companyId = SessionStorageService.getItem('company_id');
    const directLink = this.state.directLink;
    let directLinkPartnerCompany = null;
    if (directLink) {
      if (directLink.company_id) {
        directLinkPartnerCompany = await this.selectedCompany(
          directLink.company_id,
          partnerCompanies
        );
        if (!directLinkPartnerCompany) {
          this.setState({ directLinkState: DIRECT_LINK_STATE.INVALID });
          return;
        } else if (directLink.company_id === Number(companyId)) {
          if (
            directLink.field &&
            directLink.field.id &&
            directLink.field.id !==
              Number(SessionStorageService.getItem('field_id'))
          ) {
            this.setState({
              directLinkPartnerCompany,
              fieldSwitchData: directLink.field,
            });
            this.switchCompany();
            return;
          } else {
            this.selectCompany(
              userIntegrateTableActionParam(directLinkPartnerCompany)
            );
            return;
          }
        } else {
          this.setState({
            isShowSwitchDirectLinkCompany: true,
            directLinkPartnerCompany,
            fieldSwitchData: directLink.field,
          });
          return;
        }
      }
    }
    this.showSwitchCompany();
  };

  showSwitchFieldOnAfterLogin = async () => {
    const directLink = this.state.directLink;
    if (
      directLink.field &&
      directLink.field.id &&
      directLink.field.id !== Number(SessionStorageService.getItem('field_id'))
    ) {
      const field = await this.selectedField(directLink);
      if (field) {
        this.setState({
          fieldSwitchData: directLink.field,
        });
        this.switchField();
        return;
      } else {
        this.setState({ directLinkState: DIRECT_LINK_STATE.INVALID });
        return;
      }
    } else {
      await this.directLinkJunction(directLink);
      return;
    }
  };

  selectedUser = async (user_id, company_id) => {
    const integrateUsers = await this.getUserIntegrate();
    let filteredUsers = [];
    if (user_id) {
      filteredUsers = _.filter(integrateUsers, function(u) {
        return u.id === user_id;
      });
    }
    if (filteredUsers.length === 1) {
      return filteredUsers[0];
    }
    if (company_id) {
      filteredUsers = _.filter(filteredUsers, function(u) {
        return _.get(u, 'company.partner_company_id') === company_id;
      });
    }
    if (filteredUsers.length) {
      return filteredUsers[0];
    } else {
      return undefined;
    }
  };

  selectedCompany = async (company_id, partnerCompanies) => {
    const partnerCompany = _.find(partnerCompanies, function(p) {
      return p.company.partner_company_id === company_id;
    });
    return partnerCompany;
  };

  selectedCompanyButUnconfirmed = async (partnerCompanies) => {
    const companyId = SessionStorageService.getCompanyId();
    const selectedCompany = await this.selectedCompany(
      Number(companyId),
      partnerCompanies
    );
    if (selectedCompany && _.get(selectedCompany, 'company.invitation_status') === INVITATION_STATUS.unconfirmed) {
      return true;
    }
    return false;
  };

  selectedField = async directLink => {
    const field_id = directLink.field_id;
    const loadListParam = {
      ...createFieldsDefaultSearchParams(),
      field_id,
      offset: 0,
      usage_status: [
        FIELD_COMPLETE_STATUS.RUNNING.ID,
        FIELD_COMPLETE_STATUS.COMPLETED.ID,
      ],
    };

    if (directLink.service_id === BUILDEE_SERVICES.ROAN.ID) {
      loadListParam.use_roan_flg = 1;
    }
    if (directLink.service_id === BUILDEE_SERVICES.NYUTAI.ID) {
      loadListParam.use_gk_flg = 1;
    }

    let fields = [];
    await FieldService.list(loadListParam).then(response => {
      if (response.result) {
        fields = response.data.entities.fields;
      }
    });

    const field = _.find(fields, function(f) {
      return f.id === field_id;
    });
    return field;
  };

  hasFieldPermission = async (fieldData, directLink) => {
    let serviceId = directLink.service_id;
    const companyId = getCompanyId();
    if (directLink.service_id === BUILDEE_SERVICES.COMMON.ID) {
      const result = await hasOnlyRouanPermission(companyId);
      if (result) {
        serviceId = BUILDEE_SERVICES.ROAN.ID;
      } else {
        serviceId = BUILDEE_SERVICES.CONFERENCE.ID;
      }
    }
    let permission = false;
    permission = await getPermissionByServiceIdAndCompanyId(
      serviceId,
      companyId
    );
    if (!permission) {
      return false;
    }

    permission = await this.hasServicePermission(serviceId, fieldData);
    return permission;
  };

  hasServicePermission = async (serviceId, fieldData) => {
    const field = await this.selectedField({
      service_id: serviceId,
      field_id: fieldData.id,
    });
    if (field) {
      return true;
    }
    return false;
  };

  selectField = async (fieldData, directLink) => {
    const permission = await this.hasFieldPermission(fieldData, directLink);
    if (permission) {
      const result = await saveFieldData(
        fieldData.id,
        LocalStorageService.getRoleId()
      );
      if (result) {
        await directLinkAction(directLink);
      }
    } else {
      this.setState({ directLinkState: DIRECT_LINK_STATE.INVALID });
      return;
    }
  };

  switchUser = () => {
    this.selectUser(
      userIntegrateTableActionParam(this.state.directLinkUserIntegrate)
    );
  };

  switchCompany = () => {
    this.selectCompany(
      userIntegrateTableActionParam(this.state.directLinkPartnerCompany)
    );
  };

  switchField = () => {
    this.selectField(this.state.fieldSwitchData, this.state.directLink);
  };

  goBackGreenFileService = () => {
    if (checkCallBackGreenFileService()) {
      masterSlideLoader.close();
    }
  };

  invitationCancel = () => {
    this.setState({ isShowInvitationConfirm: false, isShowSwitchCompany: true, showBtnClose: false }, () => {
      window.location.href = '/callback';
    });
  };

  invitationConfirm = async () => {
    if (this.isSubmittingFlag) {
      return;
    }
    this.isSubmittingFlag = true;
    await CompanyUserInvitationStatusService.update(
      _.get(this.state.invitationEntities, 'company_user_invitation_status.id'),
      { invitation_status: INVITATION_STATUS.confirmed }
    )
      .then(response => {
        this.setState({ isShowInvitationConfirm: false }, () => {
          if (isPartnerRoleId(this.state.roleId)) {
            this.selectCompany(this.state.selectedCompany);
          } else {
            window.location.href = '/callback';
          }
        });
      })
      .finally(() => {
        this.isSubmittingFlag = false;
      });
  };

  invitationReport = () => {
    this.setState({
      isShowInvitationConfirm: false,
      isShowMisregistrationReport: true,
    });
  };

  misregistrationCancel = () => {
    const misregistrationReportValues = _.cloneDeep(
      this.state.misregistrationReportValues
    );
    misregistrationReportValues.invitee_comment = '';
    misregistrationReportValues.errors = {};
    this.setState({
      isShowInvitationConfirm: true,
      isShowMisregistrationReport: false,
      misregistrationReportValues,
    });
  };

  misregistrationConfirm = async () => {
    const available = this.misregistrationValidate();
    if (!available) {
      return;
    }
    if (this.isSubmittingFlag) {
      return;
    }
    this.isSubmittingFlag = true;
    await CompanyUserInvitationStatusService.update(
      _.get(this.state.invitationEntities, 'company_user_invitation_status.id'),
      this.state.misregistrationReportValues
    )
      .then(response => {
        const company_users_count =
          _.get(response.data, 'entities.company_users_count') || 0;
        this.setState({ isShowMisregistrationReport: false }, () => {
          if (
            company_users_count === 0 ||
            SessionStorageService.getDirectLink()
          ) {
            AuthProvider.instance.logout();
          } else {
            window.location.href = '/callback';
          }
        });
      })
      .finally(() => {
        this.isSubmittingFlag = false;
      });
  };

  misregistrationChange = event => {
    const misregistrationReportValues = _.cloneDeep(
      this.state.misregistrationReportValues
    );
    const targetName = event.target.name;
    const targetValue = event.target.value;
    misregistrationReportValues[targetName] = deleteControlCode(targetValue);
    this.setState({ misregistrationReportValues });
  };

  misregistrationValidate = event => {
    const misregistrationReportValues = _.cloneDeep(
      this.state.misregistrationReportValues
    );
    const misregistrationReportSchema = Joi.object().keys({
      invitee_comment: Joi.string()
        .required()
        .max(500),
    });
    const validated = validateByJoi(
      misregistrationReportSchema,
      misregistrationReportValues
    );
    const available = validated.result;
    if (!_.isEmpty(event)) {
      const name = event.target.name;
      if (_.has(validated.messages, name)) {
        misregistrationReportValues.errors[name] = validated.messages[name];
      } else if (misregistrationReportValues.errors[name]) {
        delete misregistrationReportValues.errors[name];
      }
    } else {
      if (!available) {
        _.each(validated.messages, (message, key) => {
          misregistrationReportValues.errors[key] = message;
        });
      }
    }
    this.setState({ misregistrationReportValues });
    return available;
  };

  render() {
    const DropdownStyle = (closed, style) => {
      if (!closed) {
        style += ' show';
      }
      return style;
    };

    return (
      <React.Fragment>
        <div
          id='account-menu'
          className='account-menu'
          ref={node => (this.node = node)}
        >
          <span
            className='img-profile'
            onClick={() => {
              this.handleToggle();
            }}
          >
            <i className='i-profile--dark i-3x' />
          </span>
          <div
            id='account-menu-dropdown'
            className={DropdownStyle(
              this.state.closed,
              'dropdown dropdown--account-menu'
            )}
          >
            <div className='dropdown__item'>
              <div className='account-summary'>
                <div className='account-summary__body'>
                  <div className='account-summary-info'>
                    <div className='account-summary-info__image'>
                      {this.state.imageUrl != '' ? (
                        <img src={this.state.imageUrl} alt='' />
                      ) : (
                        <i className='i-profile--dark' />
                      )}
                    </div>
                    <ul className='account-summary-info__list'>
                      <li className='account-summary-info__title'>
                        {this.state.userName}
                      </li>
                      <li className='account-summary-info__item'>
                        {this.state.jobName}
                      </li>
                    </ul>
                  </div>
                </div>
                <div className='account-summary__footer'>
                  <button
                    className='account-summary__footer__btn btn'
                    onClick={() => {
                      masterSlideLoader.show('UserProfileDetail', {
                        state: { id: 0 },
                      });
                    }}
                  >
                    アカウント編集
                  </button>
                  {this.state.integrated && (
                    <button
                      className='account-summary__footer__btn btn'
                      onClick={this.toggleDialog}
                    >
                      操作ユーザー選択
                    </button>
                  )}
                  {!this.state.integrated &&
                    isPartnerRoleId(this.state.roleId) &&
                    _.size(this.state.partnerCompanies) > 1 && (
                      <button
                        className='account-summary__footer__btn btn'
                        onClick={this.toggleSwitchCompany}
                      >
                        操作ユーザー選択
                      </button>
                    )}
                  <button
                    className='account-summary__footer__btn btn btn--negative'
                    onClick={() => AuthProvider.instance.logout()}
                  >
                    ログアウト
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ChooseUserIntegrateModal
          visible={this.state.isShowSwitchUser}
          data={this.state.usersIntegrate}
          onClose={this.toggleDialog}
          action={this.selectUser}
          showBtnClose={this.state.showBtnClose}
        />
        <ChooseCompany
          visible={this.state.isShowSwitchCompany}
          data={this.state.partnerCompanies}
          onClose={this.toggleSwitchCompany}
          action={this.selectCompany}
          showBtnClose={this.state.showBtnClose}
        />
        <DirectLinkModal
          visible={this.state.isShowSwitchDirectLinkUser}
          data={[this.state.directLinkUserIntegrate]}
          fieldSwitchData={this.state.fieldSwitchData}
          onClose={this.switchUser}
        />
        <DirectLinkModal
          visible={this.state.isShowSwitchDirectLinkCompany}
          data={[this.state.directLinkPartnerCompany]}
          fieldSwitchData={this.state.fieldSwitchData}
          onClose={this.switchCompany}
        />
        <InvitationConfirmDialog
          visible={this.state.isShowInvitationConfirm}
          onCancel={this.invitationCancel}
          onLogout={() => AuthProvider.instance.logout()}
          onConfirm={this.invitationConfirm}
          onReport={this.invitationReport}
          invitationEntities={this.state.invitationEntities}
        />
        <MisregistrationReportDialog
          visible={this.state.isShowMisregistrationReport}
          onCancel={this.misregistrationCancel}
          onLogout={() => AuthProvider.instance.logout()}
          onConfirm={this.misregistrationConfirm}
          onChange={this.misregistrationChange}
          validate={this.misregistrationValidate}
          invitationEntities={this.state.invitationEntities}
          misregistrationReportValues={this.state.misregistrationReportValues}
        />
        <Information
          visible={this.state.directLinkState === DIRECT_LINK_STATE.INVALID}
          title={messageUtility.getMessage('E-001-402')}
          multilineText={true}
          message={
            <React.Fragment>
              <div>
                {messageUtility.getMessage('E-000-058')}
                <br />
                <br />
                {messageUtility.getMessage('I-000-139')}
              </div>
            </React.Fragment>
          }
          buttonText={messageUtility.getMessage('I-000-138')}
          onClose={() => {
            SessionStorageService.removeItem('afterLoginEventsFlag');
            SessionStorageService.removeItem('relayPoint');
            SessionStorageService.removeItem('directLink');
            window.location.href = '/';
          }}
        />
      </React.Fragment>
    );
  }
}

const ChooseCompany = props => {
  return <ChooseUserIntegrateModal {...props} />;
};



// WEBPACK FOOTER //
// ./src/components/AccountMenu.jsx